import React, { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  Flex,
  Heading,
  IconBox,
  Link,
  Text,
  TextField,
  VisuallyHidden,
} from '@/components/ui';
import { login as loginApi } from '@/services/api';
import { useLogout } from '@/services/authHooks';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { sessionManager } from '@/services/sessionManager';
// import { statePreserver } from '@/services/statePreserver'; // TODO: Remove state preserver completely
import {
  IconAlertTriangle,
  IconEye,
  IconEyeOff,
  IconLock,
  IconUser,
} from '@tabler/icons-react';

interface SessionExpiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionRestored: () => void;
}

export const SessionExpiredModal: React.FC<SessionExpiredModalProps> = ({
  isOpen,
  onClose,
  onSessionRestored,
}) => {
  const { user, setUser } = useAuth();
  const storedLogin =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('lastLogin') || ''
      : '';
  const currentUserLogin = user?.username || user?.email || '';
  const hasKnownLogin = !!currentUserLogin;
  const [login, setLogin] = useState(currentUserLogin || storedLogin || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{
    login?: string;
    password?: string;
  }>({});

  const { mutate: logout } = useLogout();
  const passwordRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setLogin(currentUserLogin || storedLogin || '');
      setPassword('');
      setError(null);
      setFormErrors({});
      setShowPassword(false);

      // TODO: State preservation disabled - remove this mechanism completely
    }
  }, [isOpen, currentUserLogin]);

  const handleContinueSession = async (
    e: React.FormEvent | null,
    isRetry = false
  ) => {
    e?.preventDefault();
    setError(null);
    setFormErrors({});

    if (!isRetry && !validateForm()) {
      return;
    }

    // Use the login from state if currentUserLogin is not available
    const loginToUse = currentUserLogin || login;
    if (!loginToUse) {
      setError(
        'Login information not available. Please logout and login again.'
      );
      return;
    }

    setIsLoggingIn(true);

    try {
      const userData = await loginApi({
        login: loginToUse,
        password: password,
      });

      // Success - restore session and update user context

      // Clear all React Query cache to prevent showing stale data
      queryClient.clear();

      // Update query cache with user data (like useLogin hook does)
      queryClient.setQueryData(['currentUser'], userData);

      // Transform and update AuthContext (like useLogin hook does)
      const user = {
        id: userData.id,
        email: userData.email,
        username: userData.username,
        first_name: userData.first_name,
        last_name: userData.last_name,
        current_study: userData.current_study,
        roles: userData.roles,
        permitted_studies: userData.permitted_studies,
      };
      setUser(user);

      // Notify parent component that session was restored
      onSessionRestored();
      sessionManager.notifyReauthSuccess();

      // TODO: State restoration disabled - remove this mechanism completely

      onClose();
    } catch (error) {
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('Password expired')) {
          setError(
            'Your password has expired. Please reset it before continuing.'
          );
        } else if (
          error.message.includes('Unauthorized') ||
          error.message.includes('Invalid credentials')
        ) {
          setError('Invalid password. Please check and try again.');
        } else if (error.message.includes('inactive')) {
          setError('Your account is inactive. Please contact support.');
        } else {
          setError(
            'Login failed. Please check your password and try again, or logout and try logging in again.'
          );
        }
      } else {
        setError(
          'Login failed. Please check your password and try again, or logout and try logging in again.'
        );
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    onClose();
    try {
      await logout();
      sessionManager.notifyReauthCancelled();
    } catch {
      // Best-effort: ignore logout failures in UI handler.
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Clear errors when user starts typing
  const handleInputChange = () => {
    setError(null);
    setFormErrors({});
  };

  // Check if we're offline
  const isOffline = !navigator.onLine;

  const validateForm = () => {
    const errors: { login?: string; password?: string } = {};

    // Only validate login if currentUserLogin is not available
    if (!currentUserLogin) {
      if (!login.trim()) {
        errors.login = 'Login is required';
      } else if (login.length < 3) {
        errors.login = 'Login must be at least 3 characters';
      }
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // TODO:  Slavik: Need to Improve layout

  return (
    isOpen && (
      <Dialog.Root open>
        <Dialog.Content
          size="small"
          className="session-expired-modal"
          onOpenAutoFocus={event => {
            // Prevent focus errors when modal opens
            try {
              // Focus appropriate field based on whether we have currentUserLogin
              if (!currentUserLogin) {
                // Focus login field if currentUserLogin is missing
                event.preventDefault();
                // Login field will get autoFocus via props
              } else if (passwordRef.current) {
                // Focus password field if we have currentUserLogin
                passwordRef.current.focus();
                event.preventDefault();
              } else {
                event.preventDefault();
              }
            } catch {
              // Silently handle focus errors
              event.preventDefault();
            }
          }}
        >
          <VisuallyHidden>
            <Dialog.Title>Session expired</Dialog.Title>
          </VisuallyHidden>
          <Flex
            direction="column"
            gap="4"
            pt="7"
            pb="7"
            align="center"
            style={{ textAlign: 'center' }}
          >
            <IconBox>
              <IconLock size={24} style={{ color: 'var(--error-main)' }} />
            </IconBox>
            <Flex
              direction="column"
              gap="2"
              mt="2"
              align="center"
              style={{ maxWidth: '320px' }}
            >
              <Heading size="small" weight="medium">
                Your session has expired
              </Heading>
              <Text size="2xsmall" color="gray">
                For security reasons, your session has ended. Enter your password
                to continue working, or logout to go to the login page.
              </Text>
            </Flex>

            <Flex mt="4" direction="column" gap="3" asChild>
              <form
                onSubmit={handleContinueSession}
                style={{
                  display: 'flex',
                  margin: 'auto',
                  marginTop: 'var(--space-4)',
                  textAlign: 'left',
                  maxWidth: 320,
                }}
              >
                {/* Username/Email field */}
                <TextField
                  label={'Username or Email'}
                  type="text"
                  value={login}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setLogin(e.target.value);
                    handleInputChange();
                  }}
                  disabled={hasKnownLogin} // Disable only if auth user known
                  placeholder={hasKnownLogin ? '' : 'Enter your username or email'}
                  startSlot={<IconUser />}
                  autoFocus={!hasKnownLogin}
                />

                {/* Validation Error Alert for login */}
                {formErrors.login && (
                  <Alert
                    color="error"
                    variant="surface"
                    icon={<IconAlertTriangle />}
                    title="Validation Error"
                    description={formErrors.login}
                  />
                )}

                {!hasKnownLogin && (
                  <Alert
                    color="info"
                    variant="surface"
                    icon={<IconAlertTriangle />}
                    title="Login Required"
                    description="Please enter your username or email address to continue."
                  />
                )}
                <TextField
                  label="Password"
                  ref={passwordRef}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPassword(e.target.value);
                    handleInputChange();
                  }}
                  placeholder="Enter your password"
                  startSlot={<IconLock />}
                  autoFocus
                  endSlot={
                    <Button
                      type="button"
                      isIconOnly
                      size="small"
                      variant="ghost"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? <IconEyeOff /> : <IconEye />}
                    </Button>
                  }
                />

                {/* Validation Error Alert */}
                {formErrors.password && (
                  <Alert
                    color="error"
                    variant="surface"
                    icon={<IconAlertTriangle />}
                    title="Validation Error"
                    description={formErrors.password}
                  />
                )}

                {/* Password Expired Error Alert */}
                {error &&
                  error.includes('Password expired') &&
                  !formErrors.password && (
                    <Alert
                      color="error"
                      variant="surface"
                      icon={<IconLock />}
                      title="Password Expired"
                      description={
                        <span>
                          Password expired. Please reset your password at{' '}
                          <Link
                            href="https://syncronyedc.com"
                            target="_blank"
                            underline="hover"
                            color="crimson"
                            rel="noopener noreferrer"
                            style={{
                              fontWeight: '500',
                            }}
                          >
                            syncronyedc.com
                          </Link>
                        </span>
                      }
                    />
                  )}

                {/* API Error Alert */}
                {error &&
                  !error.includes('Password expired') &&
                  !formErrors.password && (
                    <Alert
                      color="error"
                      variant="surface"
                      icon={<IconAlertTriangle />}
                      title="Login Failed"
                      description={error}
                    />
                  )}

                {/* Offline warning */}
                {isOffline && (
                  <Alert
                    color="warning"
                    variant="surface"
                    icon={<IconAlertTriangle />}
                    title="No Internet Connection"
                    description="You're currently offline. Session restoration requires an internet connection."
                  />
                )}

                {/* Retry status removed - no auto-retry */}

                {/* Buttons */}
                <Flex gap="3" align="center" justify="center" mt="4">
                  <Button
                    variant="outlined"
                    onClick={handleLogout}
                    disabled={isLoggingIn}
                    style={{ minWidth: 120 }}
                  >
                    Logout
                  </Button>

                  <Button
                    type="submit"
                    disabled={
                      isLoggingIn ||
                      !password.trim() ||
                      !!formErrors.password ||
                      !!formErrors.login ||
                      (!currentUserLogin && !login.trim()) ||
                      isOffline
                    }
                    style={{ minWidth: 120 }}
                    loading={isLoggingIn}
                    variant="classic"
                    color="primary"
                  >
                    {isOffline ? 'Waiting for connection...' : 'Continue Session'}
                  </Button>
                </Flex>
              </form>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    )
  );
};
